// QR Kiosk v2 — KV2-T1 machine-QR entry chooser. Rendered by KioskEntryGate for an
// UNAUTHENTICATED hit on /public-v2/machines/:id (the printed QR target), URL unchanged.
// Two actions: "Enter guest PIN" (GuestPinForm in LOCKED-machine mode — the machine is
// taken from the URL, never typed) and "Log in" (today's company sign-in redirect).
//
// SEC (KV2G-1/2): displays ONLY the raw #{internalNumber} from the URL — NO anonymous
// machine-identity fetch, so the screen is byte-identical whether or not the machine
// exists (no enumeration oracle). Self-contained anonymous shell exactly like
// GuestLandingV2 (imports the token sheet + wraps the scoped .tier-guest root) because
// KioskV2Layout only mounts behind the auth guard.
import "../kiosk-v2-tokens.css";

import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../shared/hooks/sharedHooks";
import { useBreakpoint } from "../../../shared/hooks/useBreakpoint";
import useTranslation from "../../../shared/hooks/useTranslation";
import { refreshAccount } from "../../../account/accountSlice";
import { buildAccountSignInWithReturn } from "../../../account/routes";
import { Glyph, GD } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";
import { GuestRedeemResult } from "./guestAccessApi";
import { storeGuestSession } from "./guestSession";
import GuestPinForm from "./GuestPinForm";

export interface GuestEntryV2Props {
  machineInternalNumber: string;
}

function Brand() {
  return (
    <span style={{ fontWeight: 800, fontSize: 17, letterSpacing: "0.02em", color: "var(--ink)", display: "inline-flex", alignItems: "center" }}>
      MACHINE
      <b style={{ color: "var(--card)", background: "var(--ink)", padding: "2px 5px", borderRadius: 4, marginLeft: 1, fontSize: 14, fontWeight: 800 }}>ID</b>
    </span>
  );
}

export function GuestEntryV2({ machineInternalNumber }: GuestEntryV2Props) {
  const { translate } = useTranslation("public");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isPhone, isTablet } = useBreakpoint();

  const entryParams = new URLSearchParams(location.search);
  const expired = entryParams.get("expired") === "1";
  // KV2-9 §6.3: the Tier 0 nameplate "Guest code" pill lands here with ?entry=pin — open
  // straight in PIN mode with the machine locked from the URL. It uses the NORMAL PIN copy;
  // only `expired` keeps its own title/hint. No behaviour change for any existing path.
  const pinFirst = entryParams.get("entry") === "pin";
  const [mode, setMode] = useState<"choose" | "pin">(expired || pinFirst ? "pin" : "choose");

  const enterKiosk = (result: GuestRedeemResult) => {
    // Store the guest token (existing kiosk mechanism), then let the gate re-render:
    // accountContext.user now resolves → the SAME URL shows the machine kiosk.
    storeGuestSession(result);
    dispatch(refreshAccount(null));
  };

  const goLogin = () => {
    const currentUrl = `${location.pathname}${location.search}${location.hash}`;
    navigate(buildAccountSignInWithReturn(currentUrl));
  };

  const columnMaxWidth: number | string = isPhone ? "100%" : isTablet ? 460 : 460;

  return (
    <div
      className="qr-kiosk-v2-root p-machineid tier-guest"
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "var(--bg)", width: "100%" }}
    >
      <div style={{ width: "100%", maxWidth: columnMaxWidth, margin: "0 auto", padding: "0 16px", display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ height: 56, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Brand />
        </div>

        <div
          className="kv2-fade"
          style={{
            background: "var(--card)",
            border: "1px solid var(--border)",
            borderRadius: 16,
            boxShadow: "var(--shadow)",
            padding: "24px 20px 22px",
            marginTop: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: "var(--tier-accent)", flexShrink: 0 }} />
            <span className="eyebrow" style={{ color: "var(--tier-accent-hov)" }}>{translate("kioskV2GuestEntryEyebrow")}</span>
          </div>

          {/* Machine identity — raw #{internalNumber} from the URL only (KV2G-1). */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 14px",
              borderRadius: 12,
              background: "var(--bg-soft)",
              border: "1px solid var(--border)",
              marginBottom: 18,
            }}
          >
            <span style={{ color: "var(--tier-accent)", display: "inline-flex", flexShrink: 0 }}>
              <Glyph d={GD.qr} size={22} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: "var(--muted)" }}>{translate("kioskV2GuestEntryMachineLabel")}</div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 18, fontWeight: 700, color: "var(--ink)", letterSpacing: "0.03em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                #{machineInternalNumber}
              </div>
            </div>
          </div>

          {mode === "choose" && (
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--ink)", lineHeight: 1.3 }}>
                {translate("kioskV2GuestEntryTitle")}
              </div>
              <div style={{ fontSize: 13.5, color: "var(--muted)", marginTop: 6, marginBottom: 18, lineHeight: 1.5 }}>
                {translate("kioskV2GuestEntryHint")}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <PrimaryButton onClick={() => setMode("pin")} label={translate("kioskV2GuestEntryPinCta")} />
                <SecondaryButton onClick={goLogin} label={translate("kioskV2GuestEntryLoginCta")} />
              </div>
            </div>
          )}

          {mode === "pin" && (
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--ink)", lineHeight: 1.3 }}>
                {expired ? translate("kioskV2GuestExpiredTitle") : translate("kioskV2GuestPinTitle")}
              </div>
              <div style={{ fontSize: 13.5, color: "var(--muted)", marginTop: 6, marginBottom: 18, lineHeight: 1.5 }}>
                {expired ? translate("kioskV2GuestEntryExpiredHint") : translate("kioskV2GuestEntryPinHint")}
              </div>
              <GuestPinForm lockedMachine={machineInternalNumber} onSuccess={enterKiosk} />
              <button
                type="button"
                onClick={() => setMode("choose")}
                style={{
                  marginTop: 14,
                  width: "100%",
                  height: 44,
                  borderRadius: 12,
                  border: 0,
                  background: "transparent",
                  color: "var(--muted)",
                  fontFamily: "inherit",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {translate("kioskV2GuestEntryBack")}
              </button>
            </div>
          )}
        </div>

        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 11.5, color: "var(--faint)", textAlign: "center", padding: "16px 0 calc(16px + env(safe-area-inset-bottom))", lineHeight: 1.5 }}>
          {translate("kioskV2GuestFooterNote")}
        </div>
      </div>
    </div>
  );
}

function PrimaryButton({ onClick, label }: { onClick: () => void; label: string }) {
  const press = usePressed();
  return (
    <button
      type="button"
      onClick={onClick}
      {...press.pressHandlers}
      style={{
        height: 52,
        borderRadius: 12,
        border: "1px solid var(--tier-accent)",
        background: "var(--tier-accent)",
        color: "var(--tier-accent-fg)",
        fontFamily: "inherit",
        fontSize: 15.5,
        fontWeight: 700,
        cursor: "pointer",
        ...press.pressStyle,
      }}
    >
      {label}
    </button>
  );
}

function SecondaryButton({ onClick, label }: { onClick: () => void; label: string }) {
  const press = usePressed();
  return (
    <button
      type="button"
      onClick={onClick}
      {...press.pressHandlers}
      style={{
        height: 50,
        borderRadius: 12,
        border: "1px solid var(--border)",
        background: "var(--card)",
        color: "var(--ink-2)",
        fontFamily: "inherit",
        fontSize: 14.5,
        fontWeight: 600,
        cursor: "pointer",
        ...press.pressStyle,
      }}
    >
      {label}
    </button>
  );
}

export default GuestEntryV2;
